import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Building2Icon,
  CheckIcon,
  CheckCircle2Icon,
  ChevronDownIcon,
  PlusIcon,
  SendIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useAuth } from "../hooks/useAuth";
import {
  createGolfCourse,
  fetchGolfCourseDetails,
  searchGolfClubs,
  updateOwnGolfCourse,
} from "../services/golfCourses";

const STEPS = [
  "Bana",
  "Tees",
  "Hål",
  "Slope",
  "Kontroll",
];

function createClientKey() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;
}

function createTee(index = 0) {
  return {
    key: createClientKey(),
    name: index === 0 ? "Gul" : "",
  };
}

function createRating() {
  return {
    menEnabled: true,
    menCourseRating: "",
    menSlopeRating: "",
    menRatingDate: "",

    womenEnabled: true,
    womenCourseRating: "",
    womenSlopeRating: "",
    womenRatingDate: "",
  };
}

function buildHoles(
  count,
  tees,
  previous = []
) {
  return Array.from(
    { length: count },
    (_, index) => {
      const holeNumber = index + 1;

      const existing = previous.find(
        (hole) =>
          Number(hole.holeNumber) ===
          holeNumber
      );

      const distances = {};

      tees.forEach((tee) => {
        distances[tee.key] =
          existing?.distances?.[tee.key] ??
          "";
      });

      return {
        holeNumber,
        par: existing?.par ?? "",
        strokeIndex:
          existing?.strokeIndex ?? "",
        distances,
      };
    }
  );
}

function detailToForm(course) {
  const tees = (course.tees || []).map(
    (tee) => ({
      key: tee.id,
      name: tee.name || "",
    })
  );

  const ratings = {};

  for (const tee of course.tees || []) {
    const men = (tee.ratings || []).find(
      (rating) =>
        rating.rating_category === "men"
    );

    const women = (tee.ratings || []).find(
      (rating) =>
        rating.rating_category === "women"
    );

    ratings[tee.id] = {
      menEnabled: Boolean(men),
      menCourseRating:
        men?.course_rating ?? "",
      menSlopeRating:
        men?.slope_rating ?? "",
      menRatingDate:
        men?.rating_date || "",

      womenEnabled: Boolean(women),
      womenCourseRating:
        women?.course_rating ?? "",
      womenSlopeRating:
        women?.slope_rating ?? "",
      womenRatingDate:
        women?.rating_date || "",
    };
  }

  const holes = (course.holes || []).map(
    (hole) => {
      let meta = null;

      for (const tee of course.tees || []) {
        meta = (tee.handicaps || []).find(
          (row) =>
            row.hole_id === hole.id
        );

        if (meta) {
          break;
        }
      }

      const distances = {};

      for (const tee of course.tees || []) {
        const distance = (
          tee.distances || []
        ).find(
          (row) =>
            row.hole_id === hole.id
        );

        distances[tee.id] =
          distance?.distance_meters ?? "";
      }

      return {
        holeNumber: Number(
          hole.hole_number
        ),
        par: meta?.par ?? "",
        strokeIndex:
          meta?.stroke_index ?? "",
        distances,
      };
    }
  );

  return {
    selectedClubId:
      course.golf_clubs?.id ||
      course.club_id ||
      null,

    clubName:
      course.golf_clubs?.name || "",
    courseName:
      course.name || "",
    city:
      course.golf_clubs?.city || "",
    countryCode:
      course.golf_clubs?.country_code ||
      "SE",
    website:
      course.golf_clubs?.website || "",

    holeCount: Number(
      course.hole_count
    ),

    tees,
    holes,
    ratings,

    sourceUrl:
      course.source_url || "",
    sourceNote:
      course.source_note || "",
  };
}

export default function AddCourse() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const editCourseId =
    searchParams.get("edit");
  const isEditMode =
    Boolean(editCourseId);

  const [loadingExisting, setLoadingExisting] =
    useState(Boolean(editCourseId));
  const [editCourseLoaded, setEditCourseLoaded] =
    useState(!editCourseId);

  const firstTee = useMemo(
    () => createTee(0),
    []
  );

  const [step, setStep] = useState(1);
  const [saving, setSaving] =
    useState(false);
  const [error, setError] = useState("");

  const [clubSuggestions, setClubSuggestions] =
    useState([]);
  const [
    loadingClubSuggestions,
    setLoadingClubSuggestions,
  ] = useState(false);
  const [showClubSuggestions, setShowClubSuggestions] =
    useState(false);

  const [form, setForm] = useState(() => ({
    selectedClubId: null,

    clubName: "",
    courseName: "",
    city: "",
    countryCode: "SE",
    website: "",

    holeCount: 18,

    tees: [firstTee],

    holes: buildHoles(
      18,
      [firstTee]
    ),

    ratings: {
      [firstTee.key]: createRating(),
    },

    sourceUrl: "",
    sourceNote: "",
  }));

  // ---------------------------------------------------------
  // Ladda befintlig egen bana vid redigering
  // ---------------------------------------------------------

  useEffect(() => {
    if (
      !editCourseId ||
      !user?.id
    ) {
      if (!editCourseId) {
        setLoadingExisting(false);
      }

      return;
    }

    let alive = true;

    async function loadExisting() {
      try {
        setLoadingExisting(true);
        setError("");

        const course =
          await fetchGolfCourseDetails(
            editCourseId
          );

        if (
          course.created_by !==
          user.id
        ) {
          throw new Error(
            "Du får bara redigera dina egna golfbanor."
          );
        }

        if (
          course.approval_status ===
          "pending"
        ) {
          throw new Error(
            "Banan granskas av HoleInOne och kan inte redigeras just nu."
          );
        }

        if (
          course.approval_status ===
          "approved"
        ) {
          throw new Error(
            "Verifierade golfbanor kan inte redigeras här."
          );
        }

        if (
          ![
            "private",
            "rejected",
          ].includes(
            course.approval_status
          )
        ) {
          throw new Error(
            "Golfbanan kan inte redigeras i sitt nuvarande läge."
          );
        }

        if (alive) {
          setForm(
            detailToForm(course)
          );
          setEditCourseLoaded(
            true
          );
        }
      } catch (err) {
        console.error(
          "Kunde inte öppna golfbanan för redigering:",
          err
        );

        if (alive) {
          setEditCourseLoaded(
            false
          );
          setError(
            err?.message ||
              "Golfbanan kunde inte öppnas för redigering."
          );
        }
      } finally {
        if (alive) {
          setLoadingExisting(false);
        }
      }
    }

    loadExisting();

    return () => {
      alive = false;
    };
  }, [
    editCourseId,
    user?.id,
  ]);

  // ---------------------------------------------------------
  // Club autocomplete
  // ---------------------------------------------------------

  useEffect(() => {
    const query = form.clubName.trim();

    if (
      isEditMode ||
      form.selectedClubId ||
      query.length < 2
    ) {
      setClubSuggestions([]);
      setLoadingClubSuggestions(false);
      return;
    }

    let alive = true;

    const timeoutId = window.setTimeout(
      async () => {
        try {
          setLoadingClubSuggestions(true);

          const result =
            await searchGolfClubs(query);

          if (alive) {
            setClubSuggestions(result);
            setShowClubSuggestions(true);
          }
        } catch (err) {
          console.error(
            "Kunde inte söka golfklubbar:",
            err
          );

          if (alive) {
            setClubSuggestions([]);
          }
        } finally {
          if (alive) {
            setLoadingClubSuggestions(
              false
            );
          }
        }
      },
      250
    );

    return () => {
      alive = false;
      window.clearTimeout(timeoutId);
    };
  }, [
    form.clubName,
    form.selectedClubId,
    isEditMode,
  ]);

  // ---------------------------------------------------------
  // Form helpers
  // ---------------------------------------------------------

  function updateField(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function updateClubName(value) {
    setForm((current) => ({
      ...current,
      selectedClubId: null,
      clubName: value,
    }));

    setShowClubSuggestions(true);
  }

  function selectClub(club) {
    setForm((current) => ({
      ...current,
      selectedClubId: club.id,
      clubName: club.name ?? "",
      city: club.city ?? "",
      countryCode:
        club.country_code ?? "SE",
      website: club.website ?? "",
    }));

    setClubSuggestions([]);
    setShowClubSuggestions(false);
  }

  function clearSelectedClub() {
    setForm((current) => ({
      ...current,
      selectedClubId: null,
    }));

    setShowClubSuggestions(true);
  }

  function setHoleCount(holeCount) {
    setForm((current) => ({
      ...current,
      holeCount,
      holes: buildHoles(
        holeCount,
        current.tees,
        current.holes
      ),
    }));
  }

  function addTee() {
    const tee = createTee(
      form.tees.length
    );

    setForm((current) => ({
      ...current,

      tees: [...current.tees, tee],

      holes: current.holes.map(
        (hole) => ({
          ...hole,
          distances: {
            ...hole.distances,
            [tee.key]: "",
          },
        })
      ),

      ratings: {
        ...current.ratings,
        [tee.key]: createRating(),
      },
    }));
  }

  function updateTee(
    key,
    field,
    value
  ) {
    setForm((current) => ({
      ...current,

      tees: current.tees.map(
        (tee) =>
          tee.key === key
            ? {
                ...tee,
                [field]: value,
              }
            : tee
      ),
    }));
  }

  function removeTee(key) {
    if (form.tees.length <= 1) {
      setError(
        "Banan måste ha minst en tee."
      );
      return;
    }

    setError("");

    setForm((current) => {
      const nextRatings = {
        ...current.ratings,
      };

      delete nextRatings[key];

      return {
        ...current,

        tees: current.tees.filter(
          (tee) => tee.key !== key
        ),

        holes: current.holes.map(
          (hole) => {
            const nextDistances = {
              ...hole.distances,
            };

            delete nextDistances[key];

            return {
              ...hole,
              distances: nextDistances,
            };
          }
        ),

        ratings: nextRatings,
      };
    });
  }

  function updateHole(
    holeNumber,
    field,
    value
  ) {
    setForm((current) => ({
      ...current,

      holes: current.holes.map(
        (hole) =>
          hole.holeNumber ===
          holeNumber
            ? {
                ...hole,
                [field]: value,
              }
            : hole
      ),
    }));
  }

  function updateDistance(
    holeNumber,
    teeKey,
    value
  ) {
    setForm((current) => ({
      ...current,

      holes: current.holes.map(
        (hole) =>
          hole.holeNumber ===
          holeNumber
            ? {
                ...hole,

                distances: {
                  ...hole.distances,
                  [teeKey]: value,
                },
              }
            : hole
      ),
    }));
  }

  function updateRating(
    teeKey,
    field,
    value
  ) {
    setForm((current) => ({
      ...current,

      ratings: {
        ...current.ratings,

        [teeKey]: {
          ...current.ratings[
            teeKey
          ],
          [field]: value,
        },
      },
    }));
  }

  // ---------------------------------------------------------
  // Validation
  // ---------------------------------------------------------

  function validateStep(
    stepNumber
  ) {
    if (stepNumber === 1) {
      if (!form.clubName.trim()) {
        return "Ange golfklubb.";
      }

      if (!form.courseName.trim()) {
        return "Ange banans namn.";
      }

      if (
        !/^[A-Za-z]{2}$/.test(
          form.countryCode.trim()
        )
      ) {
        return "Land ska anges med två bokstäver, till exempel SE.";
      }

      if (
        form.website.trim() &&
        !/^https?:\/\//i.test(
          form.website.trim()
        )
      ) {
        return "Klubbens webbplats måste börja med http:// eller https://.";
      }

      return null;
    }

    if (stepNumber === 2) {
      if (form.tees.length < 1) {
        return "Lägg till minst en tee.";
      }

      if (
        form.tees.some(
          (tee) => !tee.name.trim()
        )
      ) {
        return "Alla tees måste ha ett namn.";
      }

      const names = form.tees.map(
        (tee) =>
          tee.name
            .trim()
            .toLocaleLowerCase(
              "sv-SE"
            )
      );

      if (
        new Set(names).size !==
        names.length
      ) {
        return "Två tees kan inte ha samma namn.";
      }

      return null;
    }

    if (stepNumber === 3) {
      const strokeIndexes = [];

      for (const hole of form.holes) {
        const par = Number(
          hole.par
        );

        const strokeIndex =
          Number(hole.strokeIndex);

        if (
          !Number.isInteger(par) ||
          par < 2 ||
          par > 7
        ) {
          return `Kontrollera par på hål ${hole.holeNumber}.`;
        }

        // För vanlig WHS-data använder vi 1–18.
        // På en 9-hålsbana kan index fortfarande vara t.ex. 1,3,5...
        if (
          !Number.isInteger(
            strokeIndex
          ) ||
          strokeIndex < 1 ||
          strokeIndex > 18
        ) {
          return `Hålindex på hål ${hole.holeNumber} ska vara mellan 1 och 18.`;
        }

        strokeIndexes.push(
          strokeIndex
        );

        for (const tee of form.tees) {
          const distance =
            Number(
              hole.distances[
                tee.key
              ]
            );

          if (
            !Number.isInteger(
              distance
            ) ||
            distance <= 0 ||
            distance > 1000
          ) {
            return `Kontrollera längden från ${tee.name} på hål ${hole.holeNumber}.`;
          }
        }
      }

      if (
        new Set(strokeIndexes)
          .size !==
        strokeIndexes.length
      ) {
        return "Hålindex måste vara unikt för varje hål.";
      }

      return null;
    }

    if (stepNumber === 4) {
      for (const tee of form.tees) {
        const rating =
          form.ratings[tee.key];

        if (!rating) {
          return `Slopeinformation saknas för ${tee.name}.`;
        }

        if (
          !rating.menEnabled &&
          !rating.womenEnabled
        ) {
          return `${tee.name} måste vara slopevärderad för minst en kategori.`;
        }

        if (rating.menEnabled) {
          const courseRating =
            Number(
              rating.menCourseRating
            );

          const slopeRating =
            Number(
              rating.menSlopeRating
            );

          if (
            !Number.isFinite(
              courseRating
            ) ||
            courseRating <= 0 ||
            courseRating >= 100
          ) {
            return `Kontrollera Course Rating för herr på ${tee.name}.`;
          }

          if (
            !Number.isInteger(
              slopeRating
            ) ||
            slopeRating < 55 ||
            slopeRating > 155
          ) {
            return `Slope Rating för herr på ${tee.name} ska vara 55–155.`;
          }
        }

        if (rating.womenEnabled) {
          const courseRating =
            Number(
              rating.womenCourseRating
            );

          const slopeRating =
            Number(
              rating.womenSlopeRating
            );

          if (
            !Number.isFinite(
              courseRating
            ) ||
            courseRating <= 0 ||
            courseRating >= 100
          ) {
            return `Kontrollera Course Rating för dam på ${tee.name}.`;
          }

          if (
            !Number.isInteger(
              slopeRating
            ) ||
            slopeRating < 55 ||
            slopeRating > 155
          ) {
            return `Slope Rating för dam på ${tee.name} ska vara 55–155.`;
          }
        }
      }

      return null;
    }

    if (stepNumber === 5) {
      if (
        form.sourceUrl.trim() &&
        !/^https?:\/\//i.test(
          form.sourceUrl.trim()
        )
      ) {
        return "Källänken måste börja med http:// eller https://.";
      }

      return null;
    }

    return null;
  }

  function validateEverything() {
    for (
      let stepNumber = 1;
      stepNumber <= 5;
      stepNumber += 1
    ) {
      const validationError =
        validateStep(stepNumber);

      if (validationError) {
        return {
          stepNumber,
          message:
            validationError,
        };
      }
    }

    return null;
  }

  function goNext() {
    const validationError =
      validateStep(step);

    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");

    setStep((current) =>
      Math.min(
        STEPS.length,
        current + 1
      )
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  function goBack() {
    setError("");

    setStep((current) =>
      Math.max(1, current - 1)
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  async function saveCourse(
    submitForReview
  ) {
    const validation =
      validateEverything();

    if (validation) {
      setError(validation.message);
      setStep(validation.stepNumber);
      return;
    }

    try {
      setSaving(true);
      setError("");

      if (isEditMode) {
        await updateOwnGolfCourse({
          courseId: editCourseId,
          form,
          submitForReview,
        });
      } else {
        await createGolfCourse({
          form,
          userId: user?.id,
          submitForReview,
        });
      }

      navigate("/courses", {
        replace: true,

        state: isEditMode
          ? {
              courseUpdated: true,
              submitted:
                submitForReview,
            }
          : {
              courseCreated: true,
              submitted:
                submitForReview,
            },
      });
    } catch (err) {
      console.error(
        "Kunde inte spara golfbanan:",
        err
      );

      setError(
        err?.message ||
          "Banan kunde inte sparas. Försök igen."
      );
    } finally {
      setSaving(false);
    }
  }

  if (loadingExisting) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f3f0e7] px-4">
        <div className="rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] px-5 py-4 text-sm text-[#183d2d] shadow-sm">
          Hämtar golfbanan…
        </div>
      </div>
    );
  }

  if (
    isEditMode &&
    !editCourseLoaded
  ) {
    return (
      <div className="min-h-screen bg-[#f3f0e7] px-4 py-8 text-[#1f2d26] sm:px-6">
        <div className="mx-auto max-w-xl rounded-[28px] border border-[#d8d2c4] bg-[#fffdf8] p-6 shadow-[0_22px_65px_rgba(31,45,38,0.10)]">
          <h1 className="text-xl font-semibold text-[#183d2d]">
            Banan kan inte redigeras
          </h1>

          <p className="mt-3 text-sm leading-6 text-[#963f39]">
            {error ||
              "Golfbanan kunde inte öppnas för redigering."}
          </p>

          <button
            type="button"
            onClick={() =>
              navigate("/courses")
            }
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#183d2d] px-4 py-3 text-sm font-semibold text-white hover:bg-[#214d39]"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Till Golfbanor
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f0e7] px-4 py-8 text-[#1f2d26] sm:px-6">
      <main className="mx-auto w-full max-w-6xl">
        <button
          type="button"
          onClick={() =>
            navigate("/courses")
          }
          disabled={saving}
          className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-[#68716c] transition hover:text-[#183d2d] disabled:opacity-50"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Golfbanor
        </button>

        <section className="overflow-hidden rounded-[28px] border border-[#d8d2c4] bg-[#fffdf8] shadow-[0_22px_65px_rgba(31,45,38,0.10)]">
          <div className="relative overflow-hidden bg-[#183d2d] px-5 py-7 text-white sm:px-8 sm:py-9">
            <div className="pointer-events-none absolute -right-20 -top-28 h-72 w-72 rounded-full bg-[#b08b45]/20 blur-3xl" />

            <div className="relative">
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#d7c28e]">
                {isEditMode
                  ? "Egen golfbana"
                  : "Ny golfbana"}
              </p>

              <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
                {isEditMode
                  ? "Redigera bana"
                  : "Lägg till bana"}
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/65">
                {isEditMode
                  ? "Rätta scorekort, tees och slopeinformation. Klubbkopplingen är låst för en befintlig bana."
                  : "Lägg in klubb, tees, scorekort och officiell slopeinformation. Banan sparas privat tills du själv väljer att skicka in den."}
              </p>
            </div>
          </div>

          <StepNavigation
            step={step}
            setStep={(target) => {
              if (
                target < step &&
                !saving
              ) {
                setError("");
                setStep(target);
              }
            }}
          />

          <div className="p-4 sm:p-6">
            {error && (
              <div className="mb-5 rounded-2xl border border-[#e3beba] bg-[#fbefed] px-4 py-3 text-sm leading-6 text-[#963f39]">
                {error}
              </div>
            )}

            {step === 1 && (
              <CourseBasics
                form={form}
                updateField={
                  updateField
                }
                updateClubName={
                  updateClubName
                }
                selectClub={
                  selectClub
                }
                clearSelectedClub={
                  clearSelectedClub
                }
                clubSuggestions={
                  clubSuggestions
                }
                loadingClubSuggestions={
                  loadingClubSuggestions
                }
                showClubSuggestions={
                  showClubSuggestions
                }
                setShowClubSuggestions={
                  setShowClubSuggestions
                }
                setHoleCount={
                  setHoleCount
                }
                lockClub={
                  isEditMode
                }
              />
            )}

            {step === 2 && (
              <TeeEditor
                tees={form.tees}
                addTee={addTee}
                removeTee={
                  removeTee
                }
                updateTee={
                  updateTee
                }
              />
            )}

            {step === 3 && (
              <HoleEditor
                holes={form.holes}
                tees={form.tees}
                updateHole={
                  updateHole
                }
                updateDistance={
                  updateDistance
                }
              />
            )}

            {step === 4 && (
              <RatingEditor
                tees={form.tees}
                ratings={
                  form.ratings
                }
                updateRating={
                  updateRating
                }
              />
            )}

            {step === 5 && (
              <ReviewStep
                form={form}
                updateField={
                  updateField
                }
              />
            )}

            <div className="mt-8 flex flex-col-reverse gap-3 border-t border-[#e4ded1] pt-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                {step > 1 && (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={goBack}
                    className="inline-flex items-center gap-2 rounded-xl border border-[#d8d2c4] px-4 py-3 text-sm font-semibold text-[#68716c] transition hover:border-[#b8b09f] disabled:opacity-50"
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Tillbaka
                  </button>
                )}
              </div>

              {step <
              STEPS.length ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={goNext}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#214d39] disabled:opacity-50"
                >
                  Fortsätt
                  <ArrowRightIcon className="h-4 w-4" />
                </button>
              ) : (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      saveCourse(false)
                    }
                    className="rounded-xl border border-[#183d2d] px-5 py-3 text-sm font-semibold text-[#183d2d] transition hover:bg-[#e5ede7] disabled:opacity-50"
                  >
                    {saving
                      ? "Sparar…"
                      : isEditMode
                      ? "Spara ändringar"
                      : "Spara som min bana"}
                  </button>

                  <button
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      saveCourse(true)
                    }
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#214d39] disabled:opacity-50"
                  >
                    <SendIcon className="h-4 w-4" />

                    {saving
                      ? "Sparar…"
                      : isEditMode
                      ? "Spara och skicka till HoleInOne"
                      : "Skicka till HoleInOne"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function StepNavigation({
  step,
  setStep,
}) {
  return (
    <div className="border-b border-[#e4ded1] bg-[#f7f4ec] px-4 py-4 sm:px-6">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {STEPS.map(
          (label, index) => {
            const number =
              index + 1;

            const complete =
              step > number;

            const active =
              step === number;

            return (
              <button
                key={label}
                type="button"
                disabled={
                  number >= step
                }
                onClick={() =>
                  setStep(number)
                }
                className={`flex shrink-0 items-center gap-2 rounded-full px-3 py-2 text-xs font-semibold transition ${
                  active
                    ? "bg-[#183d2d] text-white"
                    : complete
                    ? "bg-[#e5ede7] text-[#287148] hover:bg-[#dce9df]"
                    : "cursor-default bg-white text-[#8b918d]"
                }`}
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-black/5 text-[10px]">
                  {complete ? (
                    <CheckIcon className="h-3 w-3" />
                  ) : (
                    number
                  )}
                </span>

                {label}
              </button>
            );
          }
        )}
      </div>
    </div>
  );
}

function CourseBasics({
  form,
  updateField,
  updateClubName,
  selectClub,
  clearSelectedClub,
  clubSuggestions,
  loadingClubSuggestions,
  showClubSuggestions,
  setShowClubSuggestions,
  setHoleCount,
  lockClub = false,
}) {
  return (
    <div>
      <SectionHeading
        title="Vilken bana gäller det?"
        description={
          lockClub
            ? "Golfklubben är låst när du redigerar en befintlig bana. Du kan fortfarande ändra banans namn, hål, tees, scorekort och slopeinformation."
            : "Sök gärna fram en befintlig golfklubb först. Om klubben inte finns skapas den som privat tillsammans med banan."
        }
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="relative sm:col-span-2">
          <Field label="Golfklubb *">
            <div className="relative">
              <Building2Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#949a96]" />

              <input
                value={form.clubName}
                disabled={lockClub}
                onChange={(event) =>
                  updateClubName(
                    event.target.value
                  )
                }
                onFocus={() => {
                  if (!lockClub) {
                    setShowClubSuggestions(
                      true
                    );
                  }
                }}
                placeholder="Ex. GolfStar"
                autoComplete="off"
                className={`${INPUT_CLASS} pl-10 pr-10 disabled:cursor-not-allowed disabled:bg-[#f3f1eb] disabled:text-[#777d79]`}
              />

              {!lockClub &&
                !form.selectedClubId && (
                <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#949a96]" />
              )}
            </div>
          </Field>

          {form.selectedClubId && (
            <div className="mt-2 flex items-center justify-between rounded-xl border border-[#bdd5c5] bg-[#edf5ef] px-3 py-2 text-xs text-[#245e3e]">
              <span className="inline-flex items-center gap-2">
                <CheckCircle2Icon className="h-4 w-4" />
                Befintlig klubb vald
              </span>

              {!lockClub && (
                <button
                  type="button"
                  onClick={clearSelectedClub}
                  className="inline-flex items-center gap-1 font-semibold hover:underline"
                >
                  <XIcon className="h-3.5 w-3.5" />
                  Byt
                </button>
              )}
            </div>
          )}

          {!lockClub &&
            !form.selectedClubId &&
            showClubSuggestions &&
            (clubSuggestions.length >
              0 ||
              loadingClubSuggestions) && (
              <div className="absolute z-20 mt-2 max-h-64 w-full overflow-y-auto rounded-2xl border border-[#d8d2c4] bg-white p-2 shadow-xl">
                {loadingClubSuggestions ? (
                  <p className="px-3 py-3 text-xs text-[#68716c]">
                    Söker klubbar…
                  </p>
                ) : (
                  clubSuggestions.map(
                    (club) => (
                      <button
                        key={club.id}
                        type="button"
                        onClick={() =>
                          selectClub(
                            club
                          )
                        }
                        className="flex w-full items-center justify-between gap-4 rounded-xl px-3 py-3 text-left transition hover:bg-[#f7f4ec]"
                      >
                        <div>
                          <p className="text-sm font-semibold text-[#183d2d]">
                            {club.name}
                          </p>

                          <p className="mt-0.5 text-xs text-[#7b817d]">
                            {[
                              club.city,
                              club.country_code,
                            ]
                              .filter(
                                Boolean
                              )
                              .join(
                                " · "
                              ) ||
                              "Ingen ort angiven"}
                          </p>
                        </div>

                        {club.approval_status ===
                          "approved" && (
                          <span className="shrink-0 rounded-full bg-[#e5ede7] px-2.5 py-1 text-[10px] font-semibold text-[#287148]">
                            Verifierad
                          </span>
                        )}
                      </button>
                    )
                  )
                )}
              </div>
            )}
        </div>

        <Field label="Bana *">
          <input
            value={form.courseName}
            onChange={(event) =>
              updateField(
                "courseName",
                event.target.value
              )
            }
            placeholder="Ex. Brollsta"
            className={INPUT_CLASS}
          />
        </Field>

        <Field label="Ort">
          <input
            value={form.city}
            disabled={
              lockClub ||
              Boolean(
                form.selectedClubId
              )
            }
            onChange={(event) =>
              updateField(
                "city",
                event.target.value
              )
            }
            placeholder="Ex. Bro"
            className={`${INPUT_CLASS} disabled:cursor-not-allowed disabled:bg-[#f3f1eb] disabled:text-[#777d79]`}
          />
        </Field>

        <Field label="Landkod">
          <input
            value={
              form.countryCode
            }
            disabled={
              lockClub ||
              Boolean(
                form.selectedClubId
              )
            }
            maxLength={2}
            onChange={(event) =>
              updateField(
                "countryCode",
                event.target.value.toUpperCase()
              )
            }
            placeholder="SE"
            className={`${INPUT_CLASS} disabled:cursor-not-allowed disabled:bg-[#f3f1eb] disabled:text-[#777d79]`}
          />
        </Field>

        <Field label="Klubbens webbplats">
          <input
            value={form.website}
            disabled={
              lockClub ||
              Boolean(
                form.selectedClubId
              )
            }
            onChange={(event) =>
              updateField(
                "website",
                event.target.value
              )
            }
            placeholder="https://..."
            className={`${INPUT_CLASS} disabled:cursor-not-allowed disabled:bg-[#f3f1eb] disabled:text-[#777d79]`}
          />
        </Field>
      </div>

      <div className="mt-6">
        <p className="mb-2 text-sm font-semibold text-[#1f2d26]">
          Antal hål
        </p>

        <div className="flex gap-2">
          {[9, 18].map(
            (count) => (
              <button
                key={count}
                type="button"
                onClick={() =>
                  setHoleCount(
                    count
                  )
                }
                className={`rounded-xl border px-5 py-3 text-sm font-semibold transition ${
                  form.holeCount ===
                  count
                    ? "border-[#183d2d] bg-[#183d2d] text-white"
                    : "border-[#d8d2c4] bg-white text-[#68716c] hover:border-[#b8b09f]"
                }`}
              >
                {count} hål
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function TeeEditor({
  tees,
  addTee,
  removeTee,
  updateTee,
}) {
  return (
    <div>
      <SectionHeading
        title="Vilka tees finns?"
        description="Använd banans officiella namn, till exempel 58, Gul, Röd eller Championship."
      />

      <div className="space-y-3">
        {tees.map(
          (tee, index) => (
            <div
              key={tee.key}
              className="grid gap-3 rounded-2xl border border-[#d8d2c4] bg-white p-4 sm:grid-cols-[1fr_auto]"
            >
              <Field
                label={`Tee ${
                  index + 1
                }`}
              >
                <input
                  value={tee.name}
                  onChange={(
                    event
                  ) =>
                    updateTee(
                      tee.key,
                      "name",
                      event.target
                        .value
                    )
                  }
                  placeholder="Ex. Gul, Röd eller 58"
                  className={
                    INPUT_CLASS
                  }
                />
              </Field>

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() =>
                    removeTee(
                      tee.key
                    )
                  }
                  aria-label={`Ta bort ${tee.name || "tee"}`}
                  className="flex h-[46px] w-[46px] items-center justify-center rounded-xl border border-[#e5d0cc] text-[#a0443e] transition hover:bg-[#fbefed]"
                >
                  <Trash2Icon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )
        )}
      </div>

      <button
        type="button"
        onClick={addTee}
        className="mt-4 inline-flex items-center gap-2 rounded-xl border border-[#183d2d] px-4 py-3 text-sm font-semibold text-[#183d2d] transition hover:bg-[#e5ede7]"
      >
        <PlusIcon className="h-4 w-4" />
        Lägg till tee
      </button>
    </div>
  );
}

function HoleEditor({
  holes,
  tees,
  updateHole,
  updateDistance,
}) {
  return (
    <div>
      <SectionHeading
        title="Scorekort"
        description="Ange par, hålindex och längden från varje tee. Hålindex ska vara unikt och ligga mellan 1 och 18."
      />

      <div className="mb-5 rounded-2xl border border-[#e1d6bd] bg-[#f8f1df] px-4 py-3 text-xs leading-5 text-[#755b2b]">
        Första versionen använder samma
        par och hålindex för alla tees.
        Databasen är redan förberedd för
        att senare hantera avvikelser per
        tee och herr/dam.
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {holes.map(
          (hole) => (
            <div
              key={
                hole.holeNumber
              }
              className="rounded-2xl border border-[#d8d2c4] bg-white p-4"
            >
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#183d2d] text-sm font-bold text-white">
                  {
                    hole.holeNumber
                  }
                </span>

                <div>
                  <p className="font-semibold text-[#183d2d]">
                    Hål{" "}
                    {
                      hole.holeNumber
                    }
                  </p>

                  <p className="text-[11px] text-[#858b87]">
                    Par, index och
                    längder
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Par">
                  <input
                    type="number"
                    inputMode="numeric"
                    min="2"
                    max="7"
                    value={
                      hole.par
                    }
                    onChange={(
                      event
                    ) =>
                      updateHole(
                        hole.holeNumber,
                        "par",
                        event.target
                          .value
                      )
                    }
                    className={
                      INPUT_CLASS
                    }
                  />
                </Field>

                <Field label="Hålindex">
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    max="18"
                    value={
                      hole.strokeIndex
                    }
                    onChange={(
                      event
                    ) =>
                      updateHole(
                        hole.holeNumber,
                        "strokeIndex",
                        event.target
                          .value
                      )
                    }
                    className={
                      INPUT_CLASS
                    }
                  />
                </Field>
              </div>

              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {tees.map(
                  (tee) => (
                    <Field
                      key={
                        tee.key
                      }
                      label={`${
                        tee.name ||
                        "Tee"
                      } · meter`}
                    >
                      <input
                        type="number"
                        inputMode="numeric"
                        min="1"
                        max="1000"
                        value={
                          hole.distances[
                            tee.key
                          ] ?? ""
                        }
                        onChange={(
                          event
                        ) =>
                          updateDistance(
                            hole.holeNumber,
                            tee.key,
                            event.target
                              .value
                          )
                        }
                        className={
                          INPUT_CLASS
                        }
                      />
                    </Field>
                  )
                )}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}

function RatingEditor({
  tees,
  ratings,
  updateRating,
}) {
  return (
    <div>
      <SectionHeading
        title="Course Rating & Slope"
        description="Ange den officiella banvärderingen för varje tee. En tee kan vara värderad för herr, dam eller båda."
      />

      <div className="space-y-4">
        {tees.map((tee) => {
          const rating =
            ratings[tee.key];

          return (
            <div
              key={tee.key}
              className="rounded-2xl border border-[#d8d2c4] bg-white p-5"
            >
              <div className="mb-5">
                <h3 className="font-semibold text-[#183d2d]">
                  Tee {tee.name}
                </h3>
              </div>

              <RatingCategory
                title="Herr"
                enabled={
                  rating.menEnabled
                }
                onEnabledChange={(
                  value
                ) =>
                  updateRating(
                    tee.key,
                    "menEnabled",
                    value
                  )
                }
                courseRating={
                  rating.menCourseRating
                }
                slopeRating={
                  rating.menSlopeRating
                }
                onCourseRatingChange={(
                  value
                ) =>
                  updateRating(
                    tee.key,
                    "menCourseRating",
                    value
                  )
                }
                onSlopeRatingChange={(
                  value
                ) =>
                  updateRating(
                    tee.key,
                    "menSlopeRating",
                    value
                  )
                }
              />

              <div className="my-5 border-t border-[#eee9dc]" />

              <RatingCategory
                title="Dam"
                enabled={
                  rating.womenEnabled
                }
                onEnabledChange={(
                  value
                ) =>
                  updateRating(
                    tee.key,
                    "womenEnabled",
                    value
                  )
                }
                courseRating={
                  rating.womenCourseRating
                }
                slopeRating={
                  rating.womenSlopeRating
                }
                onCourseRatingChange={(
                  value
                ) =>
                  updateRating(
                    tee.key,
                    "womenCourseRating",
                    value
                  )
                }
                onSlopeRatingChange={(
                  value
                ) =>
                  updateRating(
                    tee.key,
                    "womenSlopeRating",
                    value
                  )
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RatingCategory({
  title,
  enabled,
  onEnabledChange,
  courseRating,
  slopeRating,
  onCourseRatingChange,
  onSlopeRatingChange,
}) {
  return (
    <div>
      <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) =>
            onEnabledChange(
              event.target.checked
            )
          }
          className="h-4 w-4 accent-[#183d2d]"
        />

        {title}
      </label>

      {enabled && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field label="Course Rating">
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              min="1"
              max="99.9"
              value={courseRating}
              onChange={(event) =>
                onCourseRatingChange(
                  event.target.value
                )
              }
              placeholder="71.4"
              className={INPUT_CLASS}
            />
          </Field>

          <Field label="Slope Rating">
            <input
              type="number"
              inputMode="numeric"
              min="55"
              max="155"
              step="1"
              value={slopeRating}
              onChange={(event) =>
                onSlopeRatingChange(
                  event.target.value
                )
              }
              placeholder="132"
              className={INPUT_CLASS}
            />
          </Field>
        </div>
      )}
    </div>
  );
}

function ReviewStep({
  form,
  updateField,
}) {
  const totalPar =
    form.holes.reduce(
      (sum, hole) =>
        sum +
        Number(hole.par || 0),
      0
    );

  const ratingCount =
    form.tees.reduce(
      (count, tee) => {
        const rating =
          form.ratings[tee.key];

        return (
          count +
          (rating?.menEnabled
            ? 1
            : 0) +
          (rating?.womenEnabled
            ? 1
            : 0)
        );
      },
      0
    );

  return (
    <div>
      <SectionHeading
        title="Kontrollera banan"
        description="Spara banan som din egen eller skicka in den till HoleInOne för verifiering."
      />

      <div className="grid gap-4 md:grid-cols-4">
        <SummaryCard
          label="Klubb"
          value={form.clubName}
          subvalue={
            form.city ||
            form.countryCode
          }
        />

        <SummaryCard
          label="Bana"
          value={form.courseName}
          subvalue={`${form.holeCount} hål · Par ${totalPar}`}
        />

        <SummaryCard
          label="Tees"
          value={String(
            form.tees.length
          )}
          subvalue={form.tees
            .map(
              (tee) => tee.name
            )
            .join(", ")}
        />

        <SummaryCard
          label="Slopetabeller"
          value={String(
            ratingCount
          )}
          subvalue="Herr/dam-kombinationer"
        />
      </div>

      <div className="mt-6 rounded-2xl border border-[#d8d2c4] bg-[#f7f4ec] p-5">
        <h3 className="font-semibold text-[#183d2d]">
          Källa
        </h3>

        <p className="mt-1 text-xs leading-5 text-[#68716c]">
          Källa är frivilligt om du
          bara använder banan själv, men
          rekommenderas starkt om du
          skickar in den för
          verifiering.
        </p>

        <div className="mt-4 grid gap-4">
          <Field label="Länk till scorekort eller slopetabell">
            <input
              value={
                form.sourceUrl
              }
              onChange={(event) =>
                updateField(
                  "sourceUrl",
                  event.target.value
                )
              }
              placeholder="https://..."
              className={INPUT_CLASS}
            />
          </Field>

          <Field label="Kommentar">
            <textarea
              rows={3}
              value={
                form.sourceNote
              }
              onChange={(event) =>
                updateField(
                  "sourceNote",
                  event.target.value
                )
              }
              placeholder="Ex. Uppgifterna är hämtade från klubbens officiella scorekort."
              className={`${INPUT_CLASS} resize-none`}
            />
          </Field>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border border-[#d8d2c4] bg-white p-4">
          <p className="text-sm font-semibold text-[#183d2d]">
            Spara som min bana
          </p>

          <p className="mt-1 text-xs leading-5 text-[#68716c]">
            Banan blir en egen bana som
            du kan använda och redigera
            senare.
          </p>
        </div>

        <div className="rounded-2xl border border-[#b9cdbf] bg-[#edf5ef] p-4">
          <p className="text-sm font-semibold text-[#245e3e]">
            Skicka till HoleInOne
          </p>

          <p className="mt-1 text-xs leading-5 text-[#527160]">
            Banan skickas till HoleInOne
            för granskning och är låst
            för redigering under tiden.
          </p>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  subvalue,
}) {
  return (
    <div className="min-w-0 rounded-2xl border border-[#d8d2c4] bg-white p-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9a7735]">
        {label}
      </p>

      <p className="mt-2 truncate font-semibold text-[#183d2d]">
        {value || "—"}
      </p>

      <p className="mt-1 truncate text-xs text-[#68716c]">
        {subvalue || "—"}
      </p>
    </div>
  );
}

function SectionHeading({
  title,
  description,
}) {
  return (
    <div className="mb-6">
      <h2 className="text-xl font-semibold text-[#183d2d]">
        {title}
      </h2>

      <p className="mt-1 max-w-3xl text-sm leading-6 text-[#68716c]">
        {description}
      </p>
    </div>
  );
}

function Field({
  label,
  children,
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-[#4f5853]">
        {label}
      </span>

      {children}
    </label>
  );
}

const INPUT_CLASS =
  "w-full rounded-xl border border-[#d8d2c4] bg-white px-3.5 py-3 text-sm text-[#1f2d26] outline-none transition placeholder:text-[#a1a5a2] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10";